'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { SubAccountIdContext } from '@flipster/r230522/normal-sub/sub-account';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { Dialog } from '@flipster/react/design-system/dialog/index';
import { dialog } from '@flipster/react/design-system/dialog/layout';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Providers } from '@flipster/react/providers/providers';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { MovableModal } from '../../shared/movable-modal';
import { getSegmentPositionSide } from '../../shared/position/segment';
import { useClosePosition } from '../../shared/trade-actions';
import { TradeModeContext, type TradeMode } from '../../trade-mode/state';
import { ModalErrorBoundary } from '../../utils/modal-error-boundary';
import { CloseDetail } from '../close-detail';
import { FullCloseDetail } from '../full-close/full-close-detail';
import { MarketOnlyContext, PositionCloseModalContext, positionCloseModalBunja } from '../state';
import { LimitClosePrice } from './limit-close-price';
import { QtySlider } from './qty-slider';

const SegmentedControl = createSegmentedControl<'market' | 'limit'>();

interface Props {
  position: OpenPosition | LegacyOpenPosition;
  onMarketCloseComplete?: () => void;
  onLimitCloseComplete?: () => void;
  accountId?: number;
  tradeMode?: TradeMode;
  marketOnly?: boolean;
  /** 모달 타이틀 override. 미지정 시 "Partial close". (차트 포지션 라인 ×에서는 "Close"로 연다) */
  title?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const PartialCloseModal = ({
  position: initPosition,
  onMarketCloseComplete,
  onLimitCloseComplete,
  accountId,
  tradeMode,
  marketOnly,
  title,
  isOpen,
  onClose,
  onExit,
}: Props) => {
  return (
    <Providers
      providers={
        <>
          <SubAccountIdContext value={accountId} />
          <TradeModeContext value={tradeMode} />
          <MarketOnlyContext value={marketOnly ?? false} />
          <PositionCloseModalContext value={initPosition} />
        </>
      }
    >
      <MovableModal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
        <MovableModal.Header>
          <MovableModal.Title label={title ?? <FormattedMessage id='Assets.r250401.Partial_close' />} />
        </MovableModal.Header>
        <ModalErrorBoundary onError={onClose}>
          <PartialCloseModalContent
            onClose={onClose}
            onMarketCloseComplete={onMarketCloseComplete}
            onLimitCloseComplete={onLimitCloseComplete}
            accountId={accountId}
          />
        </ModalErrorBoundary>
      </MovableModal.Root>
    </Providers>
  );
};

const PartialCloseModalContent = ({
  onClose,
  onMarketCloseComplete,
  onLimitCloseComplete,
  accountId,
}: {
  onClose: () => void;
  onMarketCloseComplete?: () => void;
  onLimitCloseComplete?: () => void;
  accountId?: number;
}) => {
  const t = useTranslations();
  const {
    positionAtom,
    baseTabAtom,
    tabAtom,
    marketOnly,
    inputQtyAtom,
    limitPriceAtom,
    hasErrorAtom,
    canCustomClosePositionAtom,
    bboPriceAtom,
  } = useBunja(positionCloseModalBunja);

  const { closePosition, isCloseLoading } = useClosePosition({
    onMarketCloseComplete,
    onLimitCloseComplete,
    accountId,
  });

  const [, setTab] = useAtom(baseTabAtom);
  const tab = useAtomValue(tabAtom);
  const position = useAtomValue(positionAtom);
  const inputQty = useAtomValue(inputQtyAtom);
  const limitPrice = useAtomValue(limitPriceAtom);
  const bboPrice = useAtomValue(bboPriceAtom);
  const hasError = useAtomValue(hasErrorAtom);
  const canCustomClosePosition = useAtomValue(canCustomClosePositionAtom);

  const handlePositionClose = async () => {
    segment.track(CommunityEvent.OrderConfirmationDoneClick, {
      share_feed: false, // social: change this flag if sharing feed feature supports partial-closed position
      realized_pnl: Number(position.unrealizedPnl),
      realized_pnl_percentage: Number(position.calcUnrealizedPnlPercent),
      symbol: position.symbol,
      trade_type: 'perpetual',
      side: getSegmentPositionSide(position.side),
      close_immediately: false,
      close_type: 'partial_close',
      order_type: tab === 'market' ? 'market_order' : 'limit_order',
    });

    const newSize =
      position.side === 'Long'
        ? Num(position.quantity).minus(Num(inputQty))
        : Num(position.quantity).plus(Num(inputQty));

    const price = tab === 'limit' ? limitPrice : bboPrice;

    const isWarningImmediatelyClosing =
      tab === 'limit' &&
      (position.side === 'Long'
        ? Num(limitPrice).isLessThanOrEqualTo(bboPrice)
        : Num(limitPrice).isGreaterThanOrEqualTo(bboPrice));

    if (isWarningImmediatelyClosing) {
      await dialog.open({
        title: {
          label: t('Trade.r251111.Order_may_fill_immediately'),
          leftAddon: <InformationCircleIcon size={24} color={token('colors.content.state.warning')} />,
        },
        content: t('Trade.r251111.Your_order_may_fill_immediately_since_close_market_price'),
        buttons: {
          direction: 'vertical',
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={t('Trade.r251111.Confirm_close')}
              onClick={async () => {
                await closePosition({
                  p: position,
                  newSize: newSize.toString(),
                  orderType: tab,
                  price: price,
                });
                onClose();
              }}
            />
          ),
          secondary: <Dialog.Button hierarchy='secondary' color='neutral' label={t('Common.r250401.Cancel')} />,
        },
      });
      return;
    }

    await closePosition({ p: position, newSize: newSize.toString(), orderType: tab, price: price });
    onClose();
  };

  return (
    <>
      <MovableModal.Content>
        {!marketOnly && (
          <SegmentedControl
            value={tab}
            defaultValue='market'
            size='medium'
            css={{ width: '100%', mb: 16 }}
            onChange={(value) => {
              setTab(value);
            }}
          >
            <SegmentedControl.Item value='market'>
              <div className={css({ textStyle: 'Text/M/Strong' })}>{t('Trade.r251008.Market')}</div>
            </SegmentedControl.Item>
            <SegmentedControl.Item value='limit'>
              <div className={css({ textStyle: 'Text/M/Strong' })}>{t('Trade.r251104.Limit')}</div>
            </SegmentedControl.Item>
          </SegmentedControl>
        )}
        <div className={stack({ gap: 8 })}>
          {tab === 'limit' && <LimitClosePrice />}
          {canCustomClosePosition ? <QtySlider /> : <FullCloseDetail showOnlyFullMessage />}
          <CloseDetail mode='partial' />
        </div>
      </MovableModal.Content>
      <MovableModal.Footer>
        <MovableModal.Button
          size='large'
          hierarchy='secondary'
          color='neutral'
          onClick={onClose}
          disabled={isCloseLoading}
          label={t('Common.r250401.Cancel')}
        />
        <MovableModal.Button
          size='large'
          disabled={hasError}
          withSpinner
          onClick={handlePositionClose}
          isLoading={isCloseLoading}
          label={tab === 'market' ? t('Position_close.r250401.Close_now') : t('Trade.r251111.Set_limit_close')}
        />
      </MovableModal.Footer>
    </>
  );
};
